'use client'

// src/app/error.tsx
// Root segment error boundary. Catches render errors on any page WITHOUT its
// own error.tsx (marketing pages: /, /pricing, /learn, /blog, /proof,
// /guarantee, …) while KEEPING the root layout (nav/footer) — before this,
// those errors fell all the way to global-error.tsx, which replaces the whole
// shell with a bare recovery page (go-live audit gap). App surfaces
// (dashboard, sat, drills, …) keep their own closer boundaries.

import { useEffect } from 'react'
import Link from 'next/link'
import * as Sentry from '@sentry/nextjs'

export default function RootError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <div className="flex min-h-[60dvh] items-center justify-center px-6 py-12">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
        <h1 className="mb-2 text-xl font-semibold text-foreground">Something went wrong</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          An unexpected error occurred on this page. Our team has been notified.
        </p>
        <div className="flex justify-center gap-3">
          <button
            onClick={reset}
            className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-on-brand"
          >
            Try again
          </button>
          <Link
            href="/"
            className="rounded-md border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
          >
            Go home
          </Link>
        </div>
        {error.digest && (
          <p className="mt-4 text-xs text-muted-foreground/60">Reference: {error.digest}</p>
        )}
      </div>
    </div>
  )
}
